/**
 * InvoiceFormHeader
 *
 * Header section: invoice type, party select, dates, export type, PO number,
 * currency, exchange rate, place of supply, and the export details card.
 * Sub-components live in `InvoiceFormHeader-parts.tsx`.
 */

import {
  EXPORT_TYPE_OPTIONS,
} from './invoice-form-helpers'
import {
  PartySelectField,
  PONumberField,
  ExportDetailsCard,
} from './InvoiceFormHeader-parts'
import { getExchangeRate } from '../../../services/currency-service'
import {
  Input,
  FormField,
  DatePicker,
  Select,
} from '../../ui'

import type { UseInvoiceFormReturn } from './useInvoiceForm'
import type { InvoiceType } from '../../../types/billing'

interface InvoiceFormHeaderProps {
  form: UseInvoiceFormReturn
  isVendor: boolean
  isLoading: boolean
  invoiceTypeOptions: Array<{ value: string; label: string }>
  partyLabel: string
  onOpenQuickCreate: () => void
}

// =============================================================================
// Refresh exchange-rate handler (hook)
// =============================================================================

function useRefreshRate(form: UseInvoiceFormReturn): () => Promise<void> {
  const { currency, baseCurrency, setExchangeRate, setIsFetchingRate, setErrors } = form
  return async (): Promise<void> => {
    setIsFetchingRate(true)
    try {
      const result = await getExchangeRate(currency, baseCurrency, true)
      if (result.data?.rate) {
        setExchangeRate(result.data.rate.toFixed(2))
        setErrors((prev) => {
          const next = { ...prev }
          delete next.exchangeRate
          return next
        })
      } else {
        setErrors((prev) => ({
          ...prev,
          exchangeRate: `Could not fetch rate for ${currency}/${baseCurrency}`,
        }))
      }
    } catch {
      setErrors((prev) => ({ ...prev, exchangeRate: 'Failed to fetch exchange rate' }))
    } finally {
      setIsFetchingRate(false)
    }
  }
}

// =============================================================================
// Primary details row (first row of the Invoice Details card)
// =============================================================================

interface PrimaryDetailsRowProps {
  form: UseInvoiceFormReturn
  isVendor: boolean
  isLoading: boolean
  isEditing: boolean
  isEditingPosted: boolean
  invoiceTypeOptions: Array<{ value: string; label: string }>
  partyLabel: string
  onOpenQuickCreate: () => void
}

// Document-number fields. Vendor bills show the supplier's own bill number
// (editable) plus our internal Bill # (auto-generated on post). Sales invoices
// only show the Invoice # field once editing.
function NumberFields({
  form, isVendor, isLoading, isEditing,
}: Pick<PrimaryDetailsRowProps, 'form' | 'isVendor' | 'isLoading' | 'isEditing'>): JSX.Element {
  const {
    invoiceNumber, setInvoiceNumber, supplierBillNumber, setSupplierBillNumber,
  } = form
  return (
    <>
      {isVendor && (
        <FormField hideMessage label="Supplier Bill #">
          <Input
            disabled={isLoading}
            placeholder="Supplier's own bill number"
            value={supplierBillNumber}
            onChange={(e) => setSupplierBillNumber(e.target.value)}
          />
        </FormField>
      )}
      {(isEditing || isVendor) && (
        <FormField hideMessage label={isVendor ? 'Bill #' : 'Invoice #'}>
          <Input
            disabled={isLoading || (isVendor && !isEditing)}
            placeholder="Auto-generated on post"
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
          />
        </FormField>
      )}
    </>
  )
}

function PrimaryDetailsRow({
  form, isVendor, isLoading, isEditing, isEditingPosted,
  invoiceTypeOptions, partyLabel, onOpenQuickCreate,
}: PrimaryDetailsRowProps): JSX.Element {
  const { invoiceType, setInvoiceType, exportType, setExportType } = form
  return (
    <div className="form-grid el-gap-field">
      <NumberFields form={form} isEditing={isEditing} isLoading={isLoading} isVendor={isVendor} />
      <FormField hideMessage required label="Invoice Type">
        <Select
          hideMessage
          disabled={isLoading}
          options={invoiceTypeOptions}
          value={invoiceType}
          onChange={(value) => setInvoiceType(value as InvoiceType)}
        />
      </FormField>
      <FormField hideMessage required label="Delivery Type">
        <Select
          hideMessage
          disabled={isLoading}
          options={EXPORT_TYPE_OPTIONS}
          value={exportType}
          onChange={(value) => setExportType(value as 'goods' | 'services')}
        />
      </FormField>
      <PartySelectField
        form={form}
        isEditingPosted={isEditingPosted}
        isLoading={isLoading}
        isVendor={isVendor}
        partyLabel={partyLabel}
        onOpenQuickCreate={onOpenQuickCreate}
      />
    </div>
  )
}

// =============================================================================
// Additional details row (second row of the Invoice Details card)
// =============================================================================

interface AdditionalDetailsRowProps {
  form: UseInvoiceFormReturn
  isVendor: boolean
  isLoading: boolean
  partyLabel: string
}

function PartyRoleField({ form, isLoading }: { form: UseInvoiceFormReturn; isLoading: boolean }): JSX.Element | null {
  const { isWalkIn, partyRole, setPartyRole, roleOptions, isEditingPosted } = form
  if (isWalkIn || roleOptions.length === 0) { return null }
  return (
    <FormField hideMessage label="Party Role">
      <Select
        hideMessage
        disabled={isLoading || isEditingPosted}
        options={roleOptions}
        value={partyRole}
        onChange={setPartyRole}
      />
    </FormField>
  )
}

function AdditionalDetailsRow({ form, isVendor, isLoading, partyLabel }: AdditionalDetailsRowProps): JSX.Element {
  const { invoiceDate, setInvoiceDate, dueDate, setDueDate, placeOfSupply, setPlaceOfSupply, dateFormat } = form
  return (
    <div className="form-grid el-gap-field el-mt-sm">
      <FormField hideMessage required label="Invoice Date">
        <DatePicker dateFormat={dateFormat} disabled={isLoading} value={invoiceDate} onChange={setInvoiceDate} />
      </FormField>
      <FormField hideMessage label="Due Date">
        <DatePicker dateFormat={dateFormat} disabled={isLoading} value={dueDate} onChange={setDueDate} />
      </FormField>
      <PONumberField form={form} isLoading={isLoading} isVendor={isVendor} partyLabel={partyLabel} />
      <FormField hideMessage label="Place of Supply">
        <Input
          hideMessage
          disabled={isLoading}
          placeholder="e.g., Kerala, Maharashtra"
          type="text"
          value={placeOfSupply}
          onChange={(e) => setPlaceOfSupply(e.target.value)}
        />
      </FormField>
      <PartyRoleField form={form} isLoading={isLoading} />
    </div>
  )
}

// =============================================================================
// Invoice details card
// =============================================================================

function InvoiceDetailsCard(props: PrimaryDetailsRowProps & { form: UseInvoiceFormReturn }): JSX.Element {
  return (
    <div className="card overflow-visible">
      <h3 className="text-xs font-bold tracking-wider uppercase el-px-md py-2 bg-primary-solid text-primary-foreground rounded-t">
        Invoice Details
      </h3>
      <div className="el-p-md">
        <PrimaryDetailsRow {...props} />
        <AdditionalDetailsRow
          form={props.form}
          isLoading={props.isLoading}
          isVendor={props.isVendor}
          partyLabel={props.partyLabel}
        />
      </div>
    </div>
  )
}

// =============================================================================
// Public component
// =============================================================================

export function InvoiceFormHeader({
  form, isVendor, isLoading, invoiceTypeOptions, partyLabel, onOpenQuickCreate,
}: InvoiceFormHeaderProps): JSX.Element {
  const handleRefreshRate = useRefreshRate(form)
  const isExport = form.invoiceType === 'export'

  return (
    <>
      <InvoiceDetailsCard
        form={form}
        invoiceTypeOptions={invoiceTypeOptions}
        isEditing={form.isEditing}
        isEditingPosted={form.isEditingPosted}
        isLoading={isLoading}
        isVendor={isVendor}
        partyLabel={partyLabel}
        onOpenQuickCreate={onOpenQuickCreate}
      />
      {isExport && (
        <ExportDetailsCard
          form={form}
          isLoading={isLoading}
          onRefreshRate={() => { void handleRefreshRate() }}
        />
      )}
    </>
  )
}
